import type { BoardDto, BoardGroup, BoardMemberDto } from '../../../api/boards';

/**
 * 목록 화면의 순수 계산 — 화면 파일에서 떼어 검사가 직접 부른다 (U2 · 2026-09-15).
 *
 * ★★★ **샘플 숫자를 옮기지 않는다**(관제 지시). 여기 있는 것은 전부 서버가 준 값에서 센 것이다.
 */

export const BOARD_GROUP_ORDER: BoardGroup[] = ['company', 'division', 'team', 'mine'];

export const groupOf = (b: Pick<BoardDto, 'group'>): BoardGroup => (b.group ?? 'mine') as BoardGroup;

export interface UnreadKpi {
  /** 볼 수 있는 게시판의 안 읽은 글 합(서버가 최근 30일 창으로 센 값의 합). */
  total: number;
  /** 볼 수 있는 게시판 수. */
  boardCount: number;
  /** 안 읽은 글이 하나라도 있는 게시판 수. */
  boardsWithUnread: number;
  /** 구획별 합 — 회사 · 부서 · 팀 · 내. 구획에 게시판이 없어도 0 으로 선다(「없다」가 사실이라서). */
  byGroup: Array<{ group: BoardGroup; count: number }>;
}

/**
 * KPI 「안 읽은 글」 — 웹이 셀 수 있는 유일한 칸(서버 요청 R7 없이도 선다).
 * `unreadCount` 를 안 주는 옛 서버의 게시판은 0 이 아니라 **센 적 없음**이지만,
 * 합에서는 뺄 수밖에 없으므로 `unknown` 을 따로 알린다.
 */
export function computeUnreadKpi(boards: ReadonlyArray<Pick<BoardDto, 'group' | 'unreadCount'>>): UnreadKpi & { unknown: boolean } {
  const byGroup = new Map<BoardGroup, number>(BOARD_GROUP_ORDER.map((g) => [g, 0]));
  let total = 0;
  let withUnread = 0;
  let unknown = false;
  for (const b of boards) {
    if (b.unreadCount == null) { unknown = true; continue; }
    const n = Math.max(0, b.unreadCount);
    total += n;
    if (n > 0) withUnread += 1;
    byGroup.set(groupOf(b), (byGroup.get(groupOf(b)) ?? 0) + n);
  }
  return {
    total,
    boardCount: boards.length,
    boardsWithUnread: withUnread,
    byGroup: BOARD_GROUP_ORDER.map((group) => ({ group, count: byGroup.get(group) ?? 0 })),
    unknown,
  };
}

/** 운영자 수 — OWNER · MANAGER. */
export function countManagers(members: ReadonlyArray<Pick<BoardMemberDto, 'role'>>): number {
  return members.filter((m) => m.role === 'OWNER' || m.role === 'MANAGER').length;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * 목록 메타의 날짜 — 정본 F18 「목록의 메타(좁은 자리) MM-DD」.
 * 다른 해의 글은 해를 붙인다(`2025-12-31`) — MM-DD 만 적으면 작년 글이 올해 글로 읽힌다.
 */
export function formatListDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const md = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return d.getFullYear() === now.getFullYear() ? md : `${d.getFullYear()}-${md}`;
}

/** 이름으로 게시판을 좁힌다 — 대소문자 무시 · 앞뒤 공백 무시. */
export function filterBoardsByName<T extends Pick<BoardDto, 'name'>>(boards: readonly T[], term: string): T[] {
  const q = term.trim().toLowerCase();
  return q ? boards.filter((b) => b.name.toLowerCase().includes(q)) : [...boards];
}

/** 회사 → 부서 → 팀 → 내 순으로 묶는다. 같은 구획 안에서는 서버 순서를 지킨다. */
export function groupBoards<T extends Pick<BoardDto, 'group'>>(boards: readonly T[]): Array<{ group: BoardGroup; boards: T[] }> {
  return BOARD_GROUP_ORDER.map((group) => ({ group, boards: boards.filter((b) => groupOf(b) === group) }));
}
